// Relative times for the queue and the waiting list, in the browser's zone (staff on the floor read wall-clock time).
// "Today 4:12 pm" / "Yesterday 4:05 pm" / "Tuesday 3:58 pm" (2–6 days) / "Mon 21 Sep 3:40 pm" (older; O03's date format).
// DAY-MONTH, by the ruling that every date in the product is day-month order (Evan, 2026-09-19): the O-screen deck
// lines moved with it ("Mon 21 Sep", "Fri 25 Sep", "Expires 31 Dec"), and staff read the same product parents do.

// Spanish for 3.0.11: the day and month words come from @kk/domain's tables for the portal's language — the same words
// the app uses — so a clubs page in Spanish reads "Hoy 4:12 p. m." and "lun 21 sep."; English is unchanged.
import { domainCopy } from '@kk/domain';

import { portalLanguage } from '@/copy/activeLanguage';

const words = () => domainCopy(portalLanguage()).time;

const startOfDay = (d: Date): number =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/** Whole local days between `at` and `now` (0 = today, 1 = yesterday, negative = in the future). */
export function daysAgo(at: Date, now: Date): number {
  return Math.round((startOfDay(now) - startOfDay(at)) / 86_400_000);
}

/** "4:12 pm" */
export function clockTime(at: Date): string {
  const h24 = at.getHours();
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  const m = String(at.getMinutes()).padStart(2, '0');
  const t = words();
  return `${h}:${m} ${h24 < 12 ? t.am : t.pm}`;
}

/** "Today" / "Yesterday" / weekday / "Mon 21 Sep" — the day word for a timestamp. */
export function dayLabel(at: Date, now: Date): string {
  const n = daysAgo(at, now);
  const t = words();
  if (n <= 0) return t.todayCapital;
  if (n === 1) return t.yesterdayCapital;
  if (n < 7) return t.weekdaysLong[at.getDay()] ?? '';
  return shortDate(at);
}

/** "Today 4:12 pm" (O01 SUBMITTED column). */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  return `${dayLabel(at, now)} ${clockTime(at)}`;
}

/** "today" / "yesterday" / "Tuesday" (O04 "Requested today · KK-7710"). */
export function relativeDay(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  const n = daysAgo(at, now);
  const t = words();
  if (n <= 0) return t.today;
  if (n === 1) return t.yesterday;
  return dayLabel(at, now);
}

/** A wire ISODate ("2026-09-21") as a local calendar day (no zone shift). */
export function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/** A local calendar day as the wire ISODate ("2026-09-21"). */
export function toIsoDate(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** "Mon 21 Sep" (O03 WHEN fields and the assignment list). */
export function shortDate(d: Date): string {
  const t = words();
  return `${t.weekdaysShort[d.getDay()] ?? ''} ${d.getDate()} ${t.monthsShort[d.getMonth()] ?? ''}`;
}

/** "Mon 21 Sep" from a wire ISODate; empty input → empty string. */
export function isoDateLabel(iso: string): string {
  return iso ? shortDate(parseIsoDate(iso)) : '';
}

/** "31 Dec" (O05 "Expires 31 Dec") from an ISO datetime. */
export function monthDay(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()} ${words().monthsShort[d.getMonth()] ?? ''}`;
}
